"use client";

import type {
  GeolocationError,
  GeolocationPosition,
} from "@/hooks/useGeolocation";
import type { CustomLocation } from "@/utils/location";
import { useCallback, useEffect, useRef } from "react";

type ReverseGeocodeResult = {
  displayName: string;
  city?: string;
  country?: string;
};

type UseLocationGeolocationFlowArgs = {
  geoPosition: GeolocationPosition | null;
  geoError: GeolocationError | null;
  isGeoLoading: boolean;
  requestLocation: () => void;
  reverseGeocode: (
    lat: number,
    lon: number,
  ) => Promise<ReverseGeocodeResult | null>;
  onCustomLocationResolved: (location: CustomLocation) => void;
};

export function useLocationGeolocationFlow({
  geoPosition,
  geoError,
  isGeoLoading,
  requestLocation,
  reverseGeocode,
  onCustomLocationResolved,
}: UseLocationGeolocationFlowArgs) {
  const geolocationRequestedRef = useRef(false);

  const handleUseMyLocation = useCallback(() => {
    geolocationRequestedRef.current = true;
    requestLocation();
  }, [requestLocation]);

  const handleGeolocationResult = useCallback(async () => {
    if (
      geolocationRequestedRef.current &&
      geoPosition &&
      !geoError &&
      !isGeoLoading
    ) {
      geolocationRequestedRef.current = false;

      const geocodeResult = await reverseGeocode(
        geoPosition.lat,
        geoPosition.lon,
      );

      if (geocodeResult) {
        onCustomLocationResolved({
          lat: geoPosition.lat,
          lon: geoPosition.lon,
          displayName: geocodeResult.displayName,
          secondaryText: geocodeResult.city || geocodeResult.country,
        });
      } else {
        onCustomLocationResolved({
          lat: geoPosition.lat,
          lon: geoPosition.lon,
          displayName: `${geoPosition.lat.toFixed(4)}, ${geoPosition.lon.toFixed(4)}`,
        });
      }
    }
  }, [
    geoPosition,
    geoError,
    isGeoLoading,
    reverseGeocode,
    onCustomLocationResolved,
  ]);

  useEffect(() => {
    if (geolocationRequestedRef.current && geoPosition && !geoError) {
      void handleGeolocationResult();
    }
  }, [geoPosition, geoError, handleGeolocationResult]);

  return {
    handleUseMyLocation,
  } as const;
}
